import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePendingChangesStore, type DiscardCallback } from '@/store/pendingChangesStore';
import { debug } from '@/utils/debug';
import type { Campaign } from '@/types/campaign';
import type { LandingPageData } from './useCampaignData';

export interface UseCampaignStateReturn {
  // Ad group selection
  selectedAdGroups: Set<string>;
  originalBidsOptimization: Map<string, boolean>;
  handleAdGroupToggle: (adGroupId: string, enabled: boolean) => void;

  // Landing page selection
  selectedLandingPages: Map<string, boolean>;
  handleLandingPageToggle: (url: string, authorized: boolean) => void;

  // Save operation tracking
  isSavingRef: React.MutableRefObject<boolean>;

  // State restoration (for discard operations)
  restoreOriginalState: () => void;

  // Update original bids after successful save
  updateOriginalBidsAfterSave: () => void;
}

interface UseCampaignStateOptions {
  campaignId: string;
  campaign: Campaign | undefined;
  displayCampaign: Campaign | undefined;
  landingPageData: LandingPageData;
  resetRemovedEntities: () => void;
}

/**
 * Hook that manages selection state for ad groups and landing pages,
 * including discard callback registration for restoring original state.
 */
export function useCampaignState({
  campaignId,
  campaign,
  displayCampaign,
  landingPageData,
  resetRemovedEntities,
}: UseCampaignStateOptions): UseCampaignStateReturn {
  const queryClient = useQueryClient();

  // Store hooks
  const queueFlouriSettingChange = usePendingChangesStore((state) => state.queueFlouriSettingChange);
  const registerDiscardCallback = usePendingChangesStore((state) => state.registerDiscardCallback);
  const unregisterDiscardCallback = usePendingChangesStore((state) => state.unregisterDiscardCallback);

  // Ad group selection state
  const [selectedAdGroups, setSelectedAdGroups] = useState<Set<string>>(new Set());
  const [originalBidsOptimization, setOriginalBidsOptimization] = useState<Map<string, boolean>>(new Map());

  // Landing page selection state
  const [selectedLandingPages, setSelectedLandingPages] = useState<Map<string, boolean>>(new Map());

  // Track when we're saving to prevent useEffect from resetting selectedAdGroups
  const isSavingRef = useRef(false);

  // Initialize selected ad groups from saved flouri_optimize_bids state
  useEffect(() => {
    // Skip reset when we're in the middle of saving
    if (isSavingRef.current) {
      debug.log('useCampaignState: Skipping ad group selection reset during save operation');
      return;
    }

    if (campaign?.ads_group_list) {
      const initial = new Set<string>();
      const originalValues = new Map<string, boolean>();

      campaign.ads_group_list.forEach((adGroup) => {
        const adGroupId = adGroup.ad_group_id || adGroup.ads_group_id || '';
        const isOptimized = adGroup.flouri_optimize_bids === true;
        originalValues.set(adGroupId, isOptimized);
        if (isOptimized) {
          initial.add(adGroupId);
        }
      });

      setSelectedAdGroups(initial);
      setOriginalBidsOptimization(originalValues);

      debug.log('useCampaignState: Initialized ad group selections from saved config', {
        total_ad_groups: campaign.ads_group_list.length,
        selected_count: initial.size,
        selected_ids: Array.from(initial)
      });
    }
  }, [campaign]);

  // Initialize landing page permissions when landing page data is extracted
  useEffect(() => {
    if (landingPageData.urlDetails.length > 0) {
      const initial = new Map<string, boolean>();
      landingPageData.urlDetails.forEach((page) => {
        initial.set(page.url, page.authorized ?? false);
      });
      setSelectedLandingPages(initial);

      debug.log('useCampaignState: Initialized landing page permissions', {
        total_pages: landingPageData.urlDetails.length,
        authorized_count: landingPageData.urlDetails.filter(p => p.authorized).length
      });
    }
  }, [landingPageData]);

  // NOTE: do NOT call `clearAllChanges()` on mount. The Campaign detail
  // page mounts every time the user navigates to it, so wiping the queue
  // here destroys any pending Meta ad-set / ad status change queued from
  // `MetaCampaignElementsList` and re-rendered as a faded badge after
  // unmount. The queue is legitimately cleared on Save (success path),
  // user-clicked Discard, or logout — those are the right places.

  // Restore original state function
  const restoreOriginalState = useCallback(() => {
    debug.log('useCampaignState: Restoring original state');

    // Restore selectedAdGroups from originalBidsOptimization
    const restoredSelection = new Set<string>();
    originalBidsOptimization.forEach((isOptimized, adGroupId) => {
      if (isOptimized) {
        restoredSelection.add(adGroupId);
      }
    });
    setSelectedAdGroups(restoredSelection);

    // Clear removed entities
    resetRemovedEntities();

    // Invalidate React Query cache to refetch fresh data
    queryClient.invalidateQueries({ queryKey: ['app-config'] });
    queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });

    debug.log('useCampaignState: Restored state after discard', {
      selectedAdGroups: Array.from(restoredSelection),
    });
  }, [originalBidsOptimization, resetRemovedEntities, queryClient, campaignId]);

  // Register discard callback
  useEffect(() => {
    const discardCallbackKey = `campaign-${campaignId}`;

    const restoreState: DiscardCallback = () => {
      restoreOriginalState();
    };

    registerDiscardCallback(discardCallbackKey, restoreState);

    return () => {
      unregisterDiscardCallback(discardCallbackKey);
    };
  }, [campaignId, restoreOriginalState, registerDiscardCallback, unregisterDiscardCallback]);

  // Ad group toggle handler
  const handleAdGroupToggle = useCallback((adGroupId: string, enabled: boolean) => {
    // Update local state immediately for responsive UI
    setSelectedAdGroups((prev) => {
      const next = new Set(prev);
      if (enabled) {
        next.add(adGroupId);
      } else {
        next.delete(adGroupId);
      }
      return next;
    });

    // Queue the change for batch save
    if (displayCampaign) {
      const originalValue = originalBidsOptimization.get(adGroupId) ?? false;

      queueFlouriSettingChange({
        entityType: 'ad_group',
        entityId: adGroupId,
        campaignId: displayCampaign.campaign_id,
        setting: 'flouri_optimize_bids',
        oldValue: originalValue,
        newValue: enabled,
      });

      debug.log('useCampaignState: Queued flouri_optimize_bids change', {
        adGroupId,
        enabled,
        originalValue,
        willBeQueued: enabled !== originalValue
      });
    }
  }, [displayCampaign, originalBidsOptimization, queueFlouriSettingChange]);

  // Landing page toggle handler
  const handleLandingPageToggle = useCallback((url: string, authorized: boolean) => {
    setSelectedLandingPages((prev) => {
      const next = new Map(prev);
      next.set(url, authorized);
      return next;
    });
  }, []);

  // Update original bids optimization after successful save
  // This ensures future toggles compare against the newly saved values
  const updateOriginalBidsAfterSave = useCallback(() => {
    const newOriginalValues = new Map<string, boolean>();

    // All currently selected ad groups are now "originally" true
    selectedAdGroups.forEach((adGroupId) => {
      newOriginalValues.set(adGroupId, true);
    });

    // All ad groups in displayCampaign that are NOT selected are "originally" false
    displayCampaign?.ads_group_list?.forEach((ag) => {
      const adGroupId = ag.ad_group_id || ag.ads_group_id || '';
      if (!selectedAdGroups.has(adGroupId)) {
        newOriginalValues.set(adGroupId, false);
      }
    });

    setOriginalBidsOptimization(newOriginalValues);

    debug.log('useCampaignState: Updated original bids after save', {
      selectedCount: selectedAdGroups.size,
      totalTracked: newOriginalValues.size,
    });
  }, [selectedAdGroups, displayCampaign]);

  return {
    selectedAdGroups,
    originalBidsOptimization,
    handleAdGroupToggle,
    selectedLandingPages,
    handleLandingPageToggle,
    isSavingRef,
    restoreOriginalState,
    updateOriginalBidsAfterSave,
  };
}
